<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fe fe-shield-off text-danger mr-2"></i>
              {{ t('messages.ban_user') }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex align-items-center mb-4 p-3 bg-light rounded">
              <div class="avatar me-3" :style="{ backgroundImage: `url(${user?.avatar})` }"></div>
              <div>
                <h6 class="mb-1">{{ user?.fullName || 'Unknown User' }}</h6>
                <small class="text-muted">ID: {{ userId }}</small>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ t('messages.ban_type') }}</label>
              <select class="form-select" v-model="banType">
                <option value="warning">{{ t('messages.warning_only') }}</option>
                <option value="temporary">{{ t('messages.temporary_ban') }}</option>
                <option value="permanent">{{ t('messages.permanent_ban') }}</option>
              </select>
            </div>

            <div class="mb-3" v-if="banType === 'temporary'">
              <label class="form-label">{{ t('messages.duration') }}</label>
              <select class="form-select" v-model="banDuration">
                <option value="1">{{ t('messages.one_day') }}</option>
                <option value="7">{{ t('messages.seven_days') }}</option>
                <option value="30">{{ t('messages.thirty_days') }}</option>
                <option value="90">{{ t('messages.ninety_days') }}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ t('messages.reason') }}</label>
              <select class="form-select" v-model="banReason">
                <option value="">{{ t('messages.select_reason') }}</option>
                <option value="spam">{{ t('messages.reason_spam') }}</option>
                <option value="harassment">{{ t('messages.reason_harassment') }}</option>
                <option value="inappropriate_content">{{ t('messages.reason_inappropriate') }}</option>
                <option value="fake_profile">{{ t('messages.reason_fake_profile') }}</option>
                <option value="scam">{{ t('messages.reason_scam') }}</option>
                <option value="multiple_accounts">{{ t('messages.reason_multiple_accounts') }}</option>
                <option value="underage">{{ t('messages.reason_underage') }}</option>
                <option value="other">{{ t('messages.reason_other') }}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ t('messages.additional_details') }}</label>
              <textarea
                class="form-control"
                v-model="banDescription"
                rows="3"
                :placeholder="t('messages.provide_context')"
                maxlength="1000"
              ></textarea>
              <small class="text-muted">
                {{ banDescription.length }}/1000
              </small>
            </div>

            <div class="alert alert-danger" v-if="banType !== 'warning'">
              <strong>{{ t('messages.warning') }}:</strong> {{ t('messages.ban_warning_text') }} {{ banType === 'permanent' ? t('messages.cannot_be_undone') : t('messages.ban_duration_days', { days: banDuration }) }}.
            </div>

            <div class="alert alert-warning" v-if="!banReason">
              {{ t('messages.select_ban_reason') }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ t('messages.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="confirmBan"
              :disabled="!banReason || isSubmitting"
            >
              <i class="fa fa-spinner fa-spin" v-if="isSubmitting"></i>
              <i class="fe fa-shield-off" v-else></i>
              {{ banType === 'warning' ? t('messages.send_warning') : t('messages.ban_user') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from '../composables/useI18n';

const { t } = useI18n();

interface BanData {
  userId: string | number;
  type: 'warning' | 'temporary' | 'permanent';
  duration?: number;
  reason: string;
  description: string;
}

interface UserInfo {
  id: string | number;
  fullName?: string;
  avatar?: string;
}

const props = defineProps<{
  userId: string | number;
  user?: UserInfo;
}>();

const emit = defineEmits<{
  (e: 'ban', data: BanData): void;
  (e: 'close'): void;
}>();

const isOpen = ref(false);
const banType = ref<'warning' | 'temporary' | 'permanent'>('warning');
const banDuration = ref('7');
const banReason = ref('');
const banDescription = ref('');
const isSubmitting = ref(false);

const openModal = () => {
  isOpen.value = true;
  resetForm();
};

const closeModal = () => {
  isOpen.value = false;
  resetForm();
  emit('close');
};

const resetForm = () => {
  banType.value = 'warning';
  banDuration.value = '7';
  banReason.value = '';
  banDescription.value = '';
  isSubmitting.value = false;
};

const confirmBan = async () => {
  if (!banReason.value) return;

  isSubmitting.value = true;

  try {
    const banData: BanData = {
      userId: props.userId,
      type: banType.value,
      reason: banReason.value,
      description: banDescription.value.trim()
    };

    if (banType.value === 'temporary') {
      banData.duration = parseInt(banDuration.value);
    }

    const response = await fetch('/admin/users/ban', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(banData)
    });

    if (response.ok) {
      const result = await response.json();
      emit('ban', { ...banData, ...result });
      closeModal();
    } else {
      const error = await response.json();
      const errorMessage = 'Failed to ban user: ' + (error.message || 'Unknown error');
      if (window.messenger?.error) {
        window.messenger.error(errorMessage);
      } else if (typeof window.bootbox !== 'undefined') {
        window.bootbox.alert(errorMessage);
      } else {
        console.error(errorMessage);
      }
    }
  } catch (error) {
    console.error('Ban user error:', error);
    const errorMessage = 'Failed to ban user. Please try again.';
    if (window.messenger?.error) {
      window.messenger.error(errorMessage);
    } else if (typeof window.bootbox !== 'undefined') {
      window.bootbox.alert(errorMessage);
    } else {
      console.error(errorMessage);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const banButtonText = computed(() => {
  switch (banType.value) {
    case 'warning':
      return t('messages.send_warning');
    case 'temporary':
      return t('messages.ban_for_days', { days: banDuration.value });
    case 'permanent':
      return t('messages.ban_permanently');
    default:
      return t('messages.ban_user');
  }
});

defineExpose({
  openModal,
  closeModal
});
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.modal.fade .modal-dialog {
  transform: translate(0, 0);
  transition: transform 0.3s ease-out;
}

.modal {
  z-index: 1050;
}

.modal-dialog {
  max-width: 550px;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f9fa;
}

.form-select:focus,
.form-control:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.alert {
  margin-bottom: 1rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
}

.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.alert-warning {
  color: #664d03;
  background-color: #fff3cd;
  border-color: #ffecb5;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.rounded {
  border-radius: 0.375rem !important;
}
</style>
